import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { Footer, MarketingHeader, ProductFactBlurb, SEO } from '@/components';
import { useIsDivorceProductEnabled } from '@/hooks/usePublicFeatureFlags';
import { track } from '@/lib/analytics';
import {
  PARENTING_PLAN_SCHEDULE_SLUGS,
  SCHEDULE_PAGE_META,
  type ParentingPlanScheduleSlug,
} from '@/lib/parenting-plan-schemas';
import { WeekGrid } from '@/components/parenting-plan';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is this parenting plan legally binding?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. This tool helps you draft language to discuss with your co-parent and review with an attorney. Courts and states have different requirements.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need an attorney?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Harbor does not provide legal advice. If you are filing with a court, a qualified family law attorney can adapt your plan to local rules.',
      },
    },
    {
      '@type': 'Question',
      name: 'What do I get for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can walk through the builder and preview your plan. We ask for email only when you download the PDF so we can send you a copy and optional app import link.',
      },
    },
  ],
};

function ParentingPlanHub() {
  const divorceEnabled = useIsDivorceProductEnabled();
  useEffect(() => {
    track('Parenting Plan Hub View');
  }, []);

  const featured: ParentingPlanScheduleSlug[] = [
    '2-2-3',
    'week-on-week-off',
    '50-50',
    '3-4-4-3',
  ];

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Free Parenting Plan Builder | Harbor'
        description='Build a clear custody schedule, holidays, communication rules, and expense language. Download a PDF and continue in the Harbor co-parenting app.'
        path='/parenting-plan'
        jsonLd={faqJsonLd}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h1
            className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Build your parenting plan
          </h1>
          <p
            className='mx-auto max-w-2xl text-xl leading-relaxed text-gray-600'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Walk through custody time, holidays, communication, and shared expenses, then download a
            printable PDF. Optional: import your schedule into Harbor when you&apos;re ready.
          </p>
          <ProductFactBlurb />
          <div className='mt-10'>
            <Link
              to='/parenting-plan/builder'
              search={{ claimToken: undefined, schedule: undefined }}
              className='inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-lg font-semibold text-white shadow-lg transition-colors hover:bg-primary-hover'
              style={{ ...nunito, fontWeight: 600 }}
            >
              Start free <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      <div className='mx-auto max-w-7xl px-6 py-16'>
        <h2 className='mb-2 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Popular schedules
        </h2>
        <p className='mb-8 max-w-3xl text-gray-600' style={nunito}>
          Start from a schedule that matches how most families describe their rotation. You can still customize details in the builder.
        </p>
        <div className='grid gap-6 md:grid-cols-2'>
          {featured.map((slug) => {
            const meta = SCHEDULE_PAGE_META[slug];
            return (
              <Link
                key={slug}
                to='/parenting-plan/$schedule'
                params={{ schedule: slug }}
                className='group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md'
              >
                <div className='border-b border-gray-100 p-4'>
                  <WeekGrid pattern={meta.pattern14} />
                </div>
                <div className='flex flex-1 flex-col p-5'>
                  <h3
                    className='text-lg font-semibold text-gray-900 group-hover:text-primary'
                    style={{ ...nunito, fontWeight: 600 }}
                  >
                    {meta.shortTitle}
                  </h3>
                  <p className='mt-2 flex-1 text-sm text-gray-600' style={nunito}>
                    {meta.description.slice(0, 140)}…
                  </p>
                  <span className='mt-4 text-sm font-semibold text-primary'>Open guide →</span>
                </div>
              </Link>
            );
          })}
        </div>

        <h2
          className='mb-4 mt-16 text-2xl font-bold text-gray-900'
          style={{ ...nunito, fontWeight: 700 }}
        >
          More templates
        </h2>
        <ul className='flex flex-wrap gap-2'>
          {PARENTING_PLAN_SCHEDULE_SLUGS.filter((s) => !featured.includes(s)).map(
            (slug) => (
              <li key={slug}>
                <Link
                  to='/parenting-plan/$schedule'
                  params={{ schedule: slug }}
                  className='inline-block rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-800 hover:border-primary hover:text-primary'
                  style={nunito}
                >
                  {SCHEDULE_PAGE_META[slug].shortTitle}
                </Link>
              </li>
            ),
          )}
        </ul>

        {divorceEnabled ? (
          <div className='mt-16 rounded-2xl bg-primary/5 p-8 text-center'>
            <h2
              className='mb-2 text-2xl font-bold text-gray-900'
              style={{ ...nunito, fontWeight: 700 }}
            >
              Going through a divorce?
            </h2>
            <p className='mx-auto mb-5 max-w-2xl text-gray-600' style={nunito}>
              Harbor Divorce walks you through official court forms for $149 and
              keeps a free checklist for your state. Educational only, not legal advice.
            </p>
            <Link
              to='/divorce'
              className='inline-block rounded-full bg-primary px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90'
              style={nunito}
            >
              See Harbor Divorce
            </Link>
          </div>
        ) : null}
      </div>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/parenting-plan/')({
  component: ParentingPlanHub,
});
